import React, { useState, useEffect } from 'react';
import { DateRangePills } from './AnalyticsFilters';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Users, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

import { CoachingStatsBar } from './coaching/CoachingStatsBar';
import { AgentCoachingCard } from './coaching/AgentCoachingCard';
import { OperationalSearchButton } from '../search/OperationalSearchModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoachingTab = ({ domainId = null, onAgentClick = null }) => {
  const { userIndustry } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [urgencyFilter, setUrgencyFilter] = useState(null); // null = all
  const [days, setDays] = useState(null); // null = all time; int preset or 'from:to' custom
  // Spec Sep-2026: Coaching Queue defaults to the last 7 days.
  useEffect(() => { setDays(7); }, []);
  // Deep-link support — the Performance-by-Site card click-throughs to
  // `?tab=coaching-queue&site=Chermside`. Read the site off the URL here
  // so the roster is filtered before the initial fetch.
  const siteFilter = searchParams.get('site') || '';

  useEffect(() => {
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    else if (userIndustry) p.set('call_type', userIndustry);
    if (siteFilter) p.set('site', siteFilter);
    if (days) p.set('days', String(days));
    const qs = p.toString() ? `?${p}` : '';
    axios.get(`${API}/analytics/coaching-queue${qs}`, { timeout: 35000 })
      .then(r => setData(r.data))
      .catch(() => setData({ queue: [] }))
      .finally(() => setLoading(false));
  }, [userIndustry, domainId, siteFilter, days]);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
    </div>
  );

  const queue = data?.queue || [];
  const high = queue.filter(a => a.urgency === 'high').length;
  const medium = queue.filter(a => a.urgency === 'medium').length;
  const onTrack = queue.filter(a => a.urgency === 'on_track').length;
  const needsCoaching = data?.total_agents_needing_coaching ?? queue.filter(a => !a.above_threshold).length;
  const filteredQueue = urgencyFilter ? queue.filter(a => a.urgency === urgencyFilter) : queue;

  return (
    <div className="space-y-6" data-testid="coaching-tab">
      <DateRangePills days={days} onChange={setDays} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        {siteFilter ? (
          <div
            className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-3 py-1.5 text-xs font-semibold text-teal-800"
            data-testid="coaching-site-filter-pill"
          >
            Filtered to {siteFilter}
            <button
              type="button"
              onClick={() => setSearchParams((prev) => {
                const n = new URLSearchParams(prev);
                n.delete('site');
                return n;
              }, { replace: true })}
              className="hover:text-teal-950"
              aria-label="Clear site filter"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : <span />}
        <OperationalSearchButton variant="ghost" data-testid="coaching-operational-search" />
      </div>
      <CoachingStatsBar
        total={needsCoaching}
        high={high}
        medium={medium}
        onTrack={onTrack}
        threshold={data?.threshold ?? 70}
        urgencyFilter={urgencyFilter}
        onUrgencyFilter={setUrgencyFilter}
      />

      {queue.length === 0 && (
        <Card className="bg-white/5 border border-white/10 rounded-xl">
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-3 text-white/40" />
            <p className="text-lg font-medium text-white">No graded calls yet</p>
            <p className="text-sm text-white/60 mt-1">Once calls are graded, coaching insights for every agent will appear here.</p>
          </CardContent>
        </Card>
      )}

      {urgencyFilter && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-white/60">Showing: <strong className="text-white">{urgencyFilter.replace('_', ' ')}</strong> ({filteredQueue.length} agents)</span>
          <button onClick={() => setUrgencyFilter(null)} className="text-xs text-white/40 hover:text-white underline">Clear filter</button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredQueue.map(agent => (
          <AgentCoachingCard key={agent.agent_id} agent={agent} onAgentClick={onAgentClick} />
        ))}
      </div>
    </div>
  );
};

export default CoachingTab;
